import { useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Μικρό κόκκινο badge με αριθμό (π.χ. ειδοποιήσεις). Κρύβεται όταν count ≤ 0.
 * Με `pulse` παίζει ένα ping/pulse animation (σαν iOS notification dot).
 */
export function Badge({
  count,
  style,
  pulse = false,
}: {
  count: number;
  style?: StyleProp<ViewStyle>;
  pulse?: boolean;
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!pulse || count <= 0) return;
    const loop = Animated.loop(
      Animated.timing(anim, {
        toValue: 1,
        duration: 1500,
        easing: Easing.out(Easing.ease),
        useNativeDriver: Platform.OS !== 'web',
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse, count, anim]);

  if (count <= 0) return null;

  const scale = anim.interpolate({ inputRange: [0, 1], outputRange: [1, 2.4] });
  const opacity = anim.interpolate({ inputRange: [0, 1], outputRange: [0.55, 0] });

  return (
    <View style={[styles.wrap, style]}>
      {pulse && (
        <Animated.View
          pointerEvents="none"
          style={[styles.ring, { transform: [{ scale }], opacity }]}
        />
      )}
      <View style={styles.badge}>
        <Text style={styles.text}>{count > 99 ? '99+' : count}</Text>
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    wrap: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    ring: {
      position: 'absolute',
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: colors.danger,
    },
    badge: {
      minWidth: 20,
      height: 20,
      borderRadius: 10,
      paddingHorizontal: 6,
      backgroundColor: colors.danger,
      alignItems: 'center',
      justifyContent: 'center',
    },
    text: {
      color: '#FFFFFF',
      fontSize: 12,
      fontWeight: '700',
    },
  });
}
